'use client';

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { PieChart, Pie, Cell } from 'recharts';

const COLORS = [
    '#3366CC',
    '#DC3912',
    '#FF9900',
    '#109618',
    '#990099',
    '#3B3EAC',
    '#0099C6',
    '#DD4477',
    '#66AA00',
    '#B82E2E',
];

const RecycledWasteChart = forwardRef(({ jobId, onSaved, onValidityChange }, ref) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            try {
                const optionsRes = await fetch(`/jobs/${jobId}/recycle-waste-options`, { credentials: 'same-origin' });
                const optionsData = await optionsRes.json();
                const options = optionsData?.data ?? [];

                let savedItems = [];
                try {
                    const savedRes = await fetch(`/jobs/${jobId}/recycle-waste-saved`, { credentials: 'same-origin' });
                    if (savedRes.ok) {
                        const savedData = await savedRes.json();
                        savedItems = savedData?.data ?? [];
                    }
                } catch {
                    // no saved data yet
                }

                const mapped = options.map((opt) => {
                    const existing = savedItems.find((item) => item.id === opt.id);

                    return {
                        id: opt.id,
                        name: opt.name ?? opt.label,
                        value: existing?.amount ?? 0,
                    };
                });

                setItems(mapped);
            } catch (err) {
                console.error('Failed to load recycled waste data', err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [jobId]);

    /* ---------------- Validation ---------------- */

    const total = items.reduce((sum, i) => sum + i.value, 0);

    useEffect(() => {
        const isValid = total === 100;
        onValidityChange?.(isValid);
    }, [total, onValidityChange]);

    /* ---------------- Update values ---------------- */

    const updateItem = (index, value) => {
        const numericValue = Number(value) || 0;
        const updated = [...items];

        const otherTotal = items.reduce((sum, item, i) => (i === index ? sum : sum + item.value), 0);

        const maxAllowed = Math.max(0, 100 - otherTotal);
        updated[index].value = Math.min(numericValue, maxAllowed);

        setItems(updated);
    };

    const saveWaste = async () => {
        if (isSaving) {
            return false;
        }

        if (total !== 100) {
            toastr.error('Recycled waste must total 100% before saving.');
            return false;
        }

        setIsSaving(true);

        try {
            const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            const itemsToSave = items.filter((item) => item.value > 0);

            const res = await fetch(`/jobs/${jobId}/recycle-waste`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': token,
                },
                credentials: 'same-origin',
                body: JSON.stringify({ recycle_waste: itemsToSave }),
            });


            if (!res.ok) throw new Error('Save failed');

            toastr.success('Recycle waste saved successfully');
            onSaved?.(true);
            return true;
        } catch (err) {
            console.error('🔴 Error saving recycled waste:', err);
            toastr.error('Failed to save recycle waste.');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    /* -------- Expose save() to parent (Edit DTN) -------- */

    useImperativeHandle(ref, () => ({
        save: async () => {
            const result = await saveWaste();
            return result;
        },
    }));

    /* ---------------- Render ---------------- */

    if (loading) return <div>Loading…</div>;

    return (
        <>
            <div className="row pb-2">
                <div className="col-12">
                    <h6 className="py-2 pl-2 cw-bg-grey text-white">Recycled Waste</h6>
                </div>
            </div>

            <div className="row">
                <div className="col-md-4">
                    {items.map((item, index) => (
                        <div key={item.id} className="d-flex align-items-center mb-1">
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: COLORS[index % COLORS.length],
                                    marginRight: 8,
                                }}
                            />
                            <input
                                value={item.name}
                                readOnly
                                className="form-control mr-2"
                                style={{
                                    fontSize: '0.85rem',
                                    padding: '0.25rem',
                                }}
                            />
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={item.value}
                                onChange={(e) => updateItem(index, e.target.value)}
                                className="form-control mr-2"
                                style={{
                                    width: 60,
                                    fontSize: '0.85rem',
                                    padding: '0.25rem',
                                }}
                            />
                            %
                        </div>
                    ))}
                </div>

                <div className="col-md-8 d-flex justify-content-center align-items-start">
                    <div
                        style={{
                            position: 'relative',
                            width: 420,
                            height: 420,
                        }}
                    >
                        <PieChart width={420} height={420}>
                            <Pie
                                data={items}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={90}
                                outerRadius={130}
                                paddingAngle={2}
                                cx={210}
                                cy={210}
                            >
                                {items.map((_, index) => (
                                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                        </PieChart>

                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 32,
                                    fontWeight: 'bold',
                                    color: total === 100 ? 'green' : 'red',
                                }}
                            >
                                {total}%
                            </div>
                            <div
                                style={{
                                    color: total === 100 ? 'green' : 'red',
                                }}
                            >
                                {total === 100 ? 'COMPLETE' : 'INCOMPLETE'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default RecycledWasteChart;
